import React, { useState } from "react";
import {
    Table,
    Card,
    Button,
    Space,
    Input,
    Select,
    Tag,
    Modal,
    Form,
    Row,
    Col,
    Typography,
    Popconfirm,
    message,
    Drawer,
    Switch,
    Divider,
} from "antd";
import {
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    ReloadOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import apiClient from "../utils/apiClient";
import { AppSelector } from "../components/AppSelector";
import { getAllHubAppProfiles } from "../services/hubAppProfileService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const Hints = () => {
    const [searchText, setSearchText] = useState("");
    const [appFilter, setAppFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("");
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
    const [selectedHint, setSelectedHint] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const { data: appProfiles = [] } = useQuery(
        ["hub-app-profiles"],
        getAllHubAppProfiles,
        { staleTime: 5 * 60 * 1000 },
    );
    const getAppLabelById = (appId) => {
        const app = appProfiles.find((item) => item.id === appId);
        return app ? app.label : appId;
    };


    // Fetch hints
    const { data, isLoading, refetch } = useQuery(
        [
            "hints",
            currentPage,
            searchText,
            appFilter,
            activeFilter,
            sortBy,
            sortOrder,
        ],
        async () => {
            const params = new URLSearchParams({
                page: currentPage,
                limit: pageSize,
                search: searchText,
                app: appFilter,
                isActive: activeFilter,
                sortBy: sortBy,
                sortOrder: sortOrder,
            });
            const response = await apiClient.get(`/hints?${params}`);
            return response.data;
        },
        {
            keepPreviousData: true,
        },
    );

    // Create mutation
    const createMutation = useMutation(
        async (hintData) => {
            const response = await apiClient.post("/hints", hintData);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success("Hint created successfully");
                queryClient.invalidateQueries("hints");
                setEditModalVisible(false);
                form.resetFields();
                setSelectedHint(null);
            },
        },
    );

    // Update mutation
    const updateMutation = useMutation(
        async ({ id, data }) => {
            const response = await apiClient.put(`/hints/${id}`, data);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success("Hint updated successfully");
                queryClient.invalidateQueries("hints");
                setEditModalVisible(false);
                form.resetFields();
                setSelectedHint(null);
            },
        },
    );

    // Delete mutation
    const deleteMutation = useMutation(
        async (id) => {
            const response = await apiClient.delete(`/hints/${id}`);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success("Hint deleted successfully");
                queryClient.invalidateQueries("hints");
            },
        },
    );

    const handleCreate = () => {
        setSelectedHint(null);
        form.resetFields();
        setEditModalVisible(true);
    };

    const handleEdit = (record) => {
        setSelectedHint(record);
        form.setFieldsValue({
            title: record.title,
            app: record.app,
            urlPath: record.urlPath,
            targetLabel: record.targetLabel,
            targetContent: record.targetContent,
            hintContent: record.hintContent,
            isActive: record.isActive,
        });
        setEditModalVisible(true);
    };

    const handleView = (record) => {
        setSelectedHint(record);
        setViewDrawerVisible(true);
    };

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    const handleSubmit = (values) => {
        if (selectedHint) {
            updateMutation.mutate({
                id: selectedHint._id,
                data: values,
            });
        } else {
            createMutation.mutate(values);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        refetch();
    };

    const handleReset = () => {
        setSearchText("");
        setAppFilter("");
        setActiveFilter("");
        setSortBy("createdAt");
        setSortOrder("desc");
        setCurrentPage(1);
    };

    const columns = [
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => handleView(record)}
                    style={{ padding: 0, height: "auto", fontWeight: 500 }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: "App",
            dataIndex: "app",
            key: "app",
            render: (app) => <Tag color="blue">{getAppLabelById(app)}</Tag>,
        },
        {
            title: "URL Path",
            dataIndex: "urlPath",
            key: "urlPath",
            responsive: ["md"],
            ellipsis: true,
            render: (path) => (
                <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                    {path}
                </span>
            ),
        },
        {
            title: "Target Label",
            dataIndex: "targetLabel",
            key: "targetLabel",
            responsive: ["lg"],
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive) => (
                <Tag color={isActive ? "green" : "red"}>
                    {isActive ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => new Date(date).toLocaleDateString(),
            responsive: ["lg"],
            sorter: true,
        },
        {
            title: "Updated",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (date) => new Date(date).toLocaleDateString(),
            responsive: ["xl"],
            sorter: true,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                        title="View"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Edit"
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this hint?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            title="Delete"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2} className="page-title">
                Hint Configurations
            </Title>

            <Card>
                <div className="table-actions">
                    <Row gutter={[16, 16]} style={{ width: "100%" }}>
                        <Col xs={24} sm={8} md={6}>
                            <Input
                                placeholder="Search hints..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onPressEnter={handleSearch}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <AppSelector
                                value={appFilter || undefined}
                                onChange={setAppFilter}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Select
                                placeholder="Status"
                                value={activeFilter || undefined}
                                onChange={setActiveFilter}
                                style={{ width: "100%" }}
                                allowClear
                            >
                                <Select.Option value="true">
                                    Active
                                </Select.Option>
                                <Select.Option value="false">
                                    Inactive
                                </Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Space>
                                <Button type="primary" onClick={handleSearch}>
                                    Search
                                </Button>
                                <Button onClick={handleReset}>Reset</Button>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => refetch()}
                                    title="Refresh"
                                />
                            </Space>
                        </Col>
                    </Row>

                    <Divider style={{ margin: "16px 0" }} />
                    <Row gutter={[16, 16]} style={{ width: "100%" }}>
                        <Col xs={24} sm={6} md={4}>
                            <div
                                style={{
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                    color: "#666",
                                }}
                            >
                                Sort by:
                            </div>
                            <Select
                                value={sortBy}
                                onChange={setSortBy}
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="createdAt">
                                    Created Time
                                </Select.Option>
                                <Select.Option value="updatedAt">
                                    Updated Time
                                </Select.Option>
                                <Select.Option value="title">
                                    Title
                                </Select.Option>
                                <Select.Option value="app">App</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={6} md={4}>
                            <div
                                style={{
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                    color: "#666",
                                }}
                            >
                                Order:
                            </div>
                            <Select
                                value={sortOrder}
                                onChange={setSortOrder}
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="desc">
                                    Newest First
                                </Select.Option>
                                <Select.Option value="asc">
                                    Oldest First
                                </Select.Option>
                            </Select>
                        </Col>
                        <Col
                            xs={24}
                            sm={12}
                            md={8}
                            style={{ display: "flex", alignItems: "flex-end" }}
                        >
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                                style={{ width: "100%" }}
                            >
                                Create New Hint
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={data?.hints}
                    rowKey="_id"
                    loading={isLoading}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: data?.total,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page) => setCurrentPage(page),
                    }}
                    scroll={{ x: 1000 }}
                    onChange={(pagination, filters, sorter) => {
                        if (sorter && sorter.field) {
                            setSortBy(sorter.field);
                            setSortOrder(
                                sorter.order === "ascend" ? "asc" : "desc",
                            );
                        }
                    }}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                title={selectedHint ? "Edit Hint" : "Create New Hint"}
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    form.resetFields();
                    setSelectedHint(null);
                }}
                onOk={() => form.submit()}
                confirmLoading={
                    createMutation.isLoading || updateMutation.isLoading
                }
                width={900}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="title"
                                label="Hint Title"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please input hint title!",
                                    },
                                ]}
                            >
                                <Input placeholder="e.g., Product Bundle Discount Info" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="app"
                                label="App"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select an app!",
                                    },
                                ]}
                            >
                                <AppSelector placeholder="Select app" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="urlPath"
                        label="URL Path (hint shows on pages containing this path)"
                        rules={[
                            {
                                required: true,
                                message: "Please input URL path!",
                            },
                        ]}
                    >
                        <Input
                            placeholder="e.g., /products, /bundle, /checkout"
                            style={{ fontFamily: "monospace" }}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="targetLabel"
                                label="Target Label (label to show hint on hover)"
                            >
                                <Input placeholder="e.g., Save 20%, Discount Applied" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="targetContent"
                                label="Target selector (optional, additional match criteria)"
                            >
                                <Input placeholder="example .class > #itemid " />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="hintContent"
                        label="Hint Content (HTML supported)"
                        rules={[
                            {
                                required: true,
                                message: "Please input hint content!",
                            },
                        ]}
                    >
                        <TextArea
                            rows={8}
                            placeholder="Enter hint content. You can use basic HTML tags like <b>, <i>, <br>, <p>, etc.&#10;&#10;Example:&#10;<b>Bundle Discount!</b><br/>Save 20% when you buy 3 or more items together."
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Active"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                    </Form.Item>

                    <div
                        style={{
                            padding: "12px",
                            backgroundColor: "#f0f5ff",
                            borderRadius: "4px",
                            marginTop: "12px",
                            fontSize: "12px",
                            color: "#0050b3",
                        }}
                    >
                        <strong>ℹ️ Note:</strong> The hint will be shown as a
                        tooltip when customers hover over elements matching the
                        target label on the specified URL path.
                    </div>
                </Form>
            </Modal>

            {/* View Drawer */}
            <Drawer
                title="Hint Details"
                placement="right"
                onClose={() => setViewDrawerVisible(false)}
                open={viewDrawerVisible}
                width={700}
            >
                {selectedHint && (
                    <div>
                        <Space
                            direction="vertical"
                            size="large"
                            style={{ width: "100%" }}
                        >
                            <div>
                                <Title level={4}>{selectedHint.title}</Title>
                                <Space>
                                    <Tag color="blue">
                                        {getAppLabelById(selectedHint.app)}
                                    </Tag>
                                    <Tag
                                        color={
                                            selectedHint.isActive
                                                ? "green"
                                                : "red"
                                        }
                                    >
                                        {selectedHint.isActive
                                            ? "Active"
                                            : "Inactive"}
                                    </Tag>
                                </Space>
                            </div>

                            <div>
                                <Text strong>URL Path:</Text>
                                <div
                                    style={{
                                        fontFamily: "monospace",
                                        color: "#52c41a",
                                        marginTop: "8px",
                                        padding: "8px",
                                        backgroundColor: "#f6ffed",
                                        borderRadius: "4px",
                                    }}
                                >
                                    {selectedHint.urlPath}
                                </div>
                            </div>

                            <div>
                                <Text strong>Target Label:</Text>
                                <div style={{ marginTop: "8px" }}>
                                    {selectedHint.targetLabel}
                                </div>
                            </div>

                            {selectedHint.targetContent && (
                                <div>
                                    <Text strong>Target Selector:</Text>
                                    <div style={{ marginTop: "8px" }}>
                                        {selectedHint.targetContent}
                                    </div>
                                </div>
                            )}

                            <div>
                                <Text strong>Hint Content:</Text>
                                <div
                                    style={{
                                        marginTop: "8px",
                                        padding: "12px",
                                        backgroundColor: "#fafafa",
                                        borderRadius: "4px",
                                        border: "1px solid #e8e8e8",
                                        lineHeight: "1.6",
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: selectedHint.hintContent,
                                    }}
                                />
                            </div>

                            <Divider />

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Text type="secondary">Created:</Text>
                                    <div>
                                        {new Date(
                                            selectedHint.createdAt,
                                        ).toLocaleString()}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary">Last Updated:</Text>
                                    <div>
                                        {new Date(
                                            selectedHint.updatedAt,
                                        ).toLocaleString()}
                                    </div>
                                </Col>
                            </Row>

                            {selectedHint.createdBy && (
                                <div>
                                    <Text type="secondary">Created by:</Text>
                                    <div>
                                        {selectedHint.createdBy.username} (
                                        {selectedHint.createdBy.email})
                                    </div>
                                </div>
                            )}

                            {selectedHint.lastModifiedBy && (
                                <div>
                                    <Text type="secondary">
                                        Last modified by:
                                    </Text>
                                    <div>
                                        {selectedHint.lastModifiedBy.username} (
                                        {selectedHint.lastModifiedBy.email})
                                    </div>
                                </div>
                            )}

                            <Divider />

                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setViewDrawerVisible(false);
                                    handleEdit(selectedHint);
                                }}
                            >
                                Edit Hint
                            </Button>
                        </Space>
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default Hints;
